import React, { forwardRef, Ref } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import { css, cx, useTheme, FableTheme } from '@fable/theme'
import { Author, Book } from '@fable/types'

import Loader from '../loader'
import clubPickIcon from '../../assets/icons/icon-club-pick.svg'

import { routes } from '../../app/store/routes'
import { GET_BOOK_LIST } from '../../queries'
import { CMSBook, CMSBookListType } from '../../types'
import altTag from '../../utils/altTag'
import { Button, BookImage, Text } from '@fable/components'
import { useImageColor, usePreloadImage } from '@fable/hooks'

interface BookItemProps {
  listType: string
}

const PostBookListItem: React.FC<CMSBook & BookItemProps> = (props) => {
  const location = useLocation()
  const navigate = useNavigate()
  const theme = useTheme()
  const isDetailed = props.listType === CMSBookListType.detailed
  const image = usePreloadImage(props.bookCover, 360)
  const color = useImageColor(image?.src || '')

  const renderButton = () => {
    const name = 'View'
    return (
      <Button negative mini className={styles(theme).viewButton}>
        {name}
      </Button>
    )
  }

  return (
    <div
      className={cx(
        css`
          cursor: pointer;
        `
      )}
      onClick={() =>
        navigate(`${routes.book}/${props.slug}`, {
          state: { prevPath: location.pathname },
        })
      }
    >
      <div
        className={cx(
          styles(theme).book,
          isDetailed && styles(theme).bookDetailed
        )}
        style={{ backgroundColor: color }}
      >
        <div className={cx(styles(theme).bookMain)}>
          <BookImage
            image={image?.src || ''}
            altTag={altTag.storeBook({
              title: props.title,
              authors: [{ name: props.author } as Author],
            } as Book)}
            className={cx(styles(theme).bookCover)}
          />
          <div className={cx(styles(theme).bookDetails)}>
            <Text
              type="subhead"
              sizing={{ base: 'XS', mobileL: 'S' }}
              color={theme.colors.whiteFang}
              truncate={2}
              tag="h4"
            >
              {props.title}
            </Text>
            <Text type="body" sizing={{ base: 'S', mobileL: 'M' }} truncate={1}>
              {`By ${props.author}`}
            </Text>
            {renderButton()}
            {props.clubPick && (
              <img
                className={cx(styles(theme).clubPickIcon)}
                src={clubPickIcon}
                alt="Fable Club Pick"
              />
            )}
          </div>
        </div>
        {isDetailed && (
          <p className={cx(styles(theme).bookDescription)}>
            {props.shortDescription}
          </p>
        )}
      </div>
    </div>
  )
}

interface PostBookListProps {
  className?: string
  id: string
  ref: Ref<HTMLDivElement>
}

export const PostBookList: React.FC<PostBookListProps> = forwardRef<
  HTMLDivElement,
  PostBookListProps
>(({ className = '', id }, ref) => {
  const theme = useTheme()
  const { data, error, loading } = useQuery(GET_BOOK_LIST, {
    variables: { id },
  })

  if (error)
    return (
      <div className={cx(styles(theme).error)}>
        There was an issue loading this book list. Please try{' '}
        <span onClick={() => window.location.reload()}>reloading</span> the
        page.
      </div>
    )
  if (loading) return <Loader />

  const bookList = data.bookList
  const books = bookList.booksCollection.items

  const renderList = books.map((book: CMSBook, index: number) => (
    <PostBookListItem key={index} listType={bookList.type} {...book} />
  ))

  return (
    <div
      className={cx(styles(theme).books, className)}
      ref={bookList.type === 'Detailed' ? ref : null}
    >
      {renderList}
    </div>
  )
})

const styles = ({ colors }: FableTheme) => ({
  books: css`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-gap: 1px;
    grid-auto-rows: auto-fill;

    @media only screen and (max-width: 648px) {
      grid-template-columns: repeat(1, 1fr);
    }

    * {
      margin-bottom: 0 !important;
    }

    span {
      color: ${colors.whiteFang} !important;
    }
  `,
  book: css`
    margin-bottom: 0 !important;
    padding: 30px;
    height: 100%;
    position: relative;

    * {
      cursor: inherit;
    }

    :before {
      content: '';
      width: 100%;
      height: 100%;
      background-color: ${colors.whiteFang};
      position: absolute;
      top: 0;
      left: 0;
      opacity: 0;
      transition: opacity ease 0.2s;
    }

    :hover::before {
      opacity: 0.15;
    }
  `,
  bookDetailed: css`
    min-height: 329px;

    @media only screen and (min-width: 961px) and (max-width: 1248px) {
      min-height: initial;
    }

    @media only screen and (max-width: 648px) {
      min-height: initial;
    }
  `,
  bookMain: css`
    display: flex;
    align-items: stretch;
    position: relative;
  `,
  bookCover: css`
    width: 96px;
    height: 138px;
    border-radius: 4px;
    position: relative;
    overflow: hidden;
    background-color: ${colors.whiteSmoke};
    box-shadow: 0px 20px 33px rgba(0, 0, 0, 0.09),
      0px 15.0399px 13.7866px rgba(0, 0, 0, 0.0503198),
      0px 8.04107px 7.37098px rgba(0, 0, 0, 0.0417275),
      0px 4.50776px 4.13211px rgba(0, 0, 0, 0.035),
      0px 2.39404px 2.19453px rgba(0, 0, 0, 0.0282725);

    img {
      margin: 0 !important;
      width: 100%;
    }

    .loader svg {
      width: 60px !important;
    }

    @media only screen and (max-width: 648px) {
      min-height: initial;
    }
  `,
  bookDetails: css`
    margin-left: 26px;
    width: calc(100% - 26px - 96px);
    position: relative;
    bottom: 0;
    display: flex;
    justify-content: center;
    flex-direction: column;

    button.border {
      background: transparent !important;
    }

    button.border:hover {
      border-color: ${colors.whiteFang} !important;
      color: ${colors.blackSwan} !important;
    }
  `,
  bookDescription: css`
    position: relative;
    margin-top: 18px;
    font-size: 14px !important;
    line-height: 19px !important;
    color: ${colors.whiteFang} !important;
  `,
  viewButton: css`
    position: absolute;
    z-index: 1;
    bottom: 0;
    overflow: hidden;
    width: fit-content;
    padding: 0 20px;
    background: transparent;
    color: ${colors.whiteFang};

    :before {
      content: '';
      width: 100%;
      height: 100%;
      background-color: ${colors.whiteFang};
      position: absolute;
      top: 0;
      left: 0;
      transform: translateY(28px);
      z-index: -1;
      transition: transform ease 0.2s;
    }

    :hover::before {
      transform: translateY(0);
    }
  `,
  clubPickIcon: css`
    margin: 0;
    display: block;
    width: 52px;
    position: absolute;
    bottom: 0;
    right: 0;

    @media only screen and (min-width: 1248px) and (max-width: 1500px) {
      width: 42px;
      top: 0;
      bottom: auto;
    }

    @media only screen and (max-width: 768px) {
      width: 42px;
      top: 0;
      bottom: auto;
    }

    @media only screen and (max-width: 648px) {
      width: 49px;
    }
  `,
  error: css`
    width: 100%;
    background-color: ${colors.whiteSmoke};
    padding: 24px;
    border-radius: 12px;

    span {
      cursor: pointer;
      text-decoration: underline;
    }
  `,
})
